import React, { useCallback, useEffect, useMemo, useState } from "react";

import { listEncompassOrganizations } from "../encompassApi";
import { ORG_DEFAULT } from "../encompassConstants";
import { OrgAvatar } from "./OrgAvatar";

const orgImage = (org) => org?.image || org?.logoUrl || org?.logo || null;

/**
 * A row's one-line state, which is the pair that actually matters: how much is
 * arriving, and whether anything is narrowing who sees it.
 *
 * "Auto-sync on / nothing narrowing" is the combination that put a lender's
 * whole pipeline on a customer's board, so it is the one called out in amber.
 */
function stateOf(org) {
  if (!org.autoSyncEnabled) {
    return { tone: "", label: "Ordered loans only" };
  }
  if (org.loanVisibilityDefault === ORG_DEFAULT.TEAM_LOANS) {
    return { tone: "ok", label: "Auto-sync · our team's loans" };
  }
  if (org.loanVisibilityDefault === ORG_DEFAULT.OWN_LOANS) {
    return { tone: "ok", label: "Auto-sync · own loans" };
  }

  return { tone: "warn", label: "Auto-sync · nothing narrowing" };
}

/**
 * The Encompass organization picker.
 *
 * Deliberately plainer than the LendingPad fleet table: there is no connection
 * state machine to filter by here, so the list carries only what an operator
 * needs to spot the organization that should be looked at.
 */
export function EncompassOrgList({ env, selectedOrgId, onSelectOrg }) {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      setLoading(true);
      setError(null);

      try {
        const json = await listEncompassOrganizations(env, {
          limit: 25,
          cursor,
          search: search.trim(),
        });

        if (!json?.success) {
          throw new Error(json?.reason || "Failed to load organizations");
        }

        const rows = Array.isArray(json.data?.organizations)
          ? json.data.organizations
          : [];
        setOrganizations((current) => (append ? [...current, ...rows] : rows));
        setNextCursor(json.data?.nextCursor || null);
      } catch (loadError) {
        setError(loadError?.message || "Failed to load organizations");
      } finally {
        setLoading(false);
      }
    },
    [env, search],
  );

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  // An organization connected only through the older credential-based
  // integration has no EPC token and never appears in the listing. Pasting its
  // id opens it anyway, which is the whole reason this is here.
  const pastedId = useMemo(() => {
    const query = search.trim();
    const looksLikeId = /^[A-Za-z0-9_-]{15,}$/.test(query);

    return looksLikeId && !organizations.some((org) => org.id === query)
      ? query
      : null;
  }, [search, organizations]);

  return (
    <div className="integrations-card">
      <div className="integrations-card-header">
        <div>
          <h2 className="integrations-card-title">Organizations</h2>
          <p className="integrations-card-subtitle">
            Find an organization to decide who sees its Encompass loans.
          </p>
        </div>
        <button
          type="button"
          className="integrations-btn integrations-btn-ghost"
          onClick={() => setReloadKey((value) => value + 1)}
          disabled={loading}
        >
          <span className="integrations-btn-ico" aria-hidden="true">
            ↻
          </span>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="integrations-finder">
        <div className={`integrations-search ${search ? "has-value" : ""}`}>
          <span className="integrations-search-ico" aria-hidden="true">
            ⌕
          </span>
          <input
            className="integrations-search-input"
            placeholder="Search by name or Organization ID…"
            value={search}
            autoComplete="off"
            onChange={(event) => setSearch(event.target.value)}
          />
          {search ? (
            <button
              type="button"
              className="integrations-search-clear"
              aria-label="Clear search"
              onClick={() => setSearch("")}
            >
              ✕
            </button>
          ) : null}
        </div>
      </div>

      {error ? <div className="integrations-result err">{error}</div> : null}

      <div className="integrations-fleet-list">
        {organizations.length === 0 && !pastedId ? (
          <div className="integrations-fleet-empty">
            <div className="integrations-fleet-empty-ico" aria-hidden="true">
              ⌕
            </div>
            <p>
              {loading
                ? "Searching organizations…"
                : "No organizations match your search."}
            </p>
          </div>
        ) : null}

        {organizations.map((org) => {
          const state = stateOf(org);

          return (
            <button
              key={org.id}
              type="button"
              className={`integrations-fleet-row ${
                org.id === selectedOrgId ? "selected" : ""
              }`}
              onClick={() => onSelectOrg(org)}
            >
              <OrgAvatar
                name={org.name}
                id={org.id}
                image={orgImage(org)}
                className="integrations-fleet-logo"
              />
              <span className="integrations-fleet-main">
                <span className="integrations-fleet-name">
                  {org.name || "Unnamed organization"}
                </span>
                <span className="integrations-fleet-sub">
                  <span className="integrations-mono">{org.id}</span>
                </span>
              </span>
              <span className="integrations-fleet-right">
                <span className={`integrations-people-pill ${state.tone}`}>
                  {state.label}
                </span>
              </span>
            </button>
          );
        })}

        {pastedId ? (
          <>
            <div className="integrations-fleet-group">
              Not in the Partner Connect list
            </div>
            <button
              type="button"
              className={`integrations-fleet-row ${
                pastedId === selectedOrgId ? "selected" : ""
              }`}
              onClick={() => onSelectOrg({ id: pastedId })}
            >
              <OrgAvatar
                name={pastedId}
                id={pastedId}
                className="integrations-fleet-logo unconnected"
              />
              <span className="integrations-fleet-main">
                <span className="integrations-fleet-name">Open by id</span>
                <span className="integrations-fleet-sub">
                  <span className="integrations-mono">{pastedId}</span>
                </span>
              </span>
            </button>
          </>
        ) : null}
      </div>

      {nextCursor ? (
        <button
          type="button"
          className="integrations-btn integrations-btn-ghost"
          disabled={loading}
          onClick={() => load({ cursor: nextCursor, append: true })}
        >
          Load more
        </button>
      ) : null}
    </div>
  );
}
